import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiLogInAppCheckoutEvent, apiCreateCheckoutBridgeToken } from '../utils/api';

function randomId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Shown once, right before checkout, only to visitors detected inside
// Facebook/Instagram's in-app browser (see utils/inAppBrowser.js) — never
// for anyone else. Doesn't block the purchase: "Continua comunque" proceeds
// exactly as before, this only gives the option to escape to a real browser
// first, where checkout is far more likely to actually work.
//
// 2026-08-27: opening the same URL in a real browser used to lose the
// session entirely — that browser's localStorage (see hooks/useAuth.js) has
// nothing in it, so the visitor landed logged out at the exact moment they'd
// decided to pay. "Open in Chrome"/"Copy link" now first mints a 5-minute,
// single-use bridge token (routes/checkoutClaim.js) and points at
// /checkout?t=... instead of the current page — that page redeems the token
// server-side and goes straight to Stripe, no re-login needed. Every step of
// this (shown, which choice, whether the bridge actually got used) is
// logged (see apiLogInAppCheckoutEvent) — this used to be pure guesswork.
// campaign (2026-08-28, hero_buy auto-checkout fix) — optional, forwarded
// to the bridge token so a hero_buy purchase that escapes through this
// modal to a real browser keeps its attribution (see
// backend/utils/checkoutBridgeToken.js). undefined for every existing
// caller (PlansPage.jsx, PromoBanner2TB.jsx) — harmless, same as not
// passing it at all.
export default function InAppBrowserWarning({ os, plan, campaign, onContinue, onClose }) {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);
  const [opening, setOpening] = useState(false);
  const correlationId = useRef(randomId()).current;

  useEffect(() => {
    apiLogInAppCheckoutEvent('warning_shown', plan, correlationId).catch(() => {});
  }, []); // eslint-disable-line

  async function handleOpenClick() {
    if (opening) return;
    setOpening(true);
    // Falls back to today's plain URL (same behavior as before this change)
    // if minting the token fails for any reason — a visitor who taps this
    // button should always get SOMETHING to open externally, never a dead
    // button because of a network hiccup.
    let target = window.location.href;
    try {
      const { token } = await apiCreateCheckoutBridgeToken(plan, correlationId, campaign);
      target = `${window.location.origin}/checkout?t=${token}`;
    } catch {}

    if (os === 'android') {
      // Works inside FB/IG's Android WebView — jumps straight to Chrome.
      // No equivalent trick exists for iOS's in-app WebView (Apple doesn't
      // expose one to JS), so there the button just copies the link instead.
      const u = new URL(target);
      window.location.href = `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=https;package=com.android.chrome;end`;
      return;
    }
    navigator.clipboard?.writeText(target).then(() => setCopied(true)).catch(() => {});
    setOpening(false);
  }

  function handleContinue() {
    apiLogInAppCheckoutEvent('chose_continue', plan, correlationId).catch(() => {});
    onContinue();
  }

  function handleClose() {
    apiLogInAppCheckoutEvent('closed_dismissed', plan, correlationId).catch(() => {});
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal inapp-warning-card" onClick={e => e.stopPropagation()}>
        <h3>⚠️ {t('inapp_warning_title')}</h3>
        <p className="inapp-warning-body">{t('inapp_warning_body')}</p>

        <button className="btn-gift" onClick={handleOpenClick} disabled={opening}>
          {os === 'android'
            ? t('inapp_warning_cta_android')
            : (copied ? t('copied') : t('inapp_warning_cta_copy'))}
        </button>
        {os !== 'android' && <p className="inapp-warning-hint">{t('inapp_warning_ios_hint')}</p>}

        <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
          <button className="btn-cancel" onClick={handleClose}>{t('close')}</button>
          <button className="btn-cancel" onClick={handleContinue}>{t('inapp_warning_continue')}</button>
        </div>
      </div>
    </div>
  );
}
